import { useState, useCallback } from "react";
import { Upload, X, ImageIcon, ArrowRight, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROOM_TYPES = [
  "Living Room", "Bedroom", "Kitchen", "Dining Room", "Bathroom",
  "Home Office", "Hallway", "Conservatory", "Loft", "Other"
];

const INTERIOR_STYLES = [
  "Modern", "Luxury", "Neutral", "Family", "Scandinavian",
  "Contemporary", "Classic", "Industrial", "Minimalist"
];

const PRICE_STANDARD = 65;
const PRICE_BULK = 55;
const BULK_THRESHOLD = 10;

export default function OrderPage() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const quantity = files.length;
  const unitPrice = quantity >= BULK_THRESHOLD ? PRICE_BULK : PRICE_STANDARD;
  const totalPrice = quantity * unitPrice;
  const savings = quantity >= BULK_THRESHOLD ? quantity * (PRICE_STANDARD - PRICE_BULK) : 0;

  const handleFileAdd = useCallback(async (newFiles) => {
    const validFiles = Array.from(newFiles).filter((f) => {
      const ext = f.name.split(".").pop().toLowerCase();
      return ["jpg", "jpeg", "png", "webp", "heic"].includes(ext);
    });

    if (validFiles.length === 0) {
      toast.error("Please upload JPG, PNG, or WebP images only");
      return;
    }

    setUploading(true);
    const uploaded = [];

    for (const file of validFiles) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await axios.post(`${API}/upload`, formData);
        uploaded.push({
          ...res.data,
          preview: URL.createObjectURL(file),
          roomType: "",
          style: "",
        });
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setFiles((prev) => [...prev, ...uploaded]);
    setUploading(false);
    if (uploaded.length > 0) {
      toast.success(`${uploaded.length} image${uploaded.length > 1 ? "s" : ""} uploaded`);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- API is a module constant

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    handleFileAdd(e.dataTransfer.files);
  }, [handleFileAdd]);

  const removeFile = (index) => {
    setFiles((prev) => {
      const updated = [...prev];
      if (updated[index].preview) URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const updateFile = (index, field, value) => {
    setFiles((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleCheckout = async () => {
    if (files.length === 0) {
      toast.error("Please upload at least one image");
      return;
    }
    if (!customerName.trim() || !customerEmail.trim()) {
      toast.error("Please enter your name and email");
      return;
    }

    setProcessing(true);
    try {
      // Create order
      const orderRes = await axios.post(`${API}/orders`, {
        customer_email: customerEmail,
        customer_name: customerName,
        images: files.map((f) => ({
          id: f.id,
          storage_path: f.storage_path,
          original_filename: f.original_filename,
        })),
        room_types: files.map((f) => f.roomType || "Not specified"),
        styles: files.map((f) => f.style || "Not specified"),
        quantity: files.length,
        notes: notes || null,
      });

      // Create Stripe checkout session
      const checkoutRes = await axios.post(`${API}/checkout/create-session`, {
        order_id: orderRes.data.id,
        origin_url: window.location.origin,
      });

      // Redirect to Stripe
      window.location.href = checkoutRes.data.url;
    } catch (err) {
      toast.error("Failed to create checkout. Please try again.");
      setProcessing(false);
    }
  };

  return (
    <main data-testid="order-page" className="pt-20">
      <section className="py-12 lg:py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          {/* Header */}
          <div className="mb-10">
            <p className="uppercase text-xs tracking-[0.2em] text-[#758C7B] mb-3 font-semibold">
              Order Virtual Staging
            </p>
            <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-light text-[#0D2D54] tracking-tight mb-3">
              Upload your photos
            </h1>
            <p className="text-base text-[#6B7280]">
              Upload your images, choose your styles, and checkout in minutes.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Mobile Order Summary - shown at top on small screens */}
            <div className="lg:hidden">
              <div className="bg-white border border-[#E8E8E4] p-4 flex items-center justify-between">
                <div>
                  <span className="text-sm text-[#6B7280]">{quantity} image{quantity !== 1 ? "s" : ""} &times; &pound;{unitPrice}</span>
                </div>
                <div className="text-right">
                  <span data-testid="mobile-order-total" className="font-['Outfit'] text-xl font-medium text-[#0D2D54]">&pound;{totalPrice.toFixed(2)}</span>
                  {savings > 0 && <span className="block text-xs text-[#758C7B] font-medium">Bulk discount applied</span>}
                </div>
              </div>
            </div>

            {/* Left: Upload + Images */}
            <div className="lg:col-span-2 space-y-6">
              {/* Upload Zone */}
              <div
                data-testid="upload-dropzone"
                className={`dropzone p-10 text-center cursor-pointer ${dragActive ? "active" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
                onClick={() => document.getElementById("file-input").click()}
              >
                <input
                  id="file-input"
                  type="file"
                  data-testid="file-input"
                  multiple
                  accept=".jpg,.jpeg,.png,.webp,.heic"
                  className="hidden"
                  onChange={(e) => handleFileAdd(e.target.files)}
                />
                {uploading ? (
                  <Loader2 size={32} className="mx-auto mb-3 text-[#0D2D54] animate-spin" />
                ) : (
                  <Upload size={32} className="mx-auto mb-3 text-[#0D2D54]" />
                )}
                <p className="text-sm font-medium text-[#0D2D54] mb-1">
                  {uploading ? "Uploading..." : "Drag & drop your images here"}
                </p>
                <p className="text-xs text-[#6B7280]">
                  or click to browse. JPG, PNG, WebP accepted. Max 50MB each.
                </p>
              </div>

              {/* Uploaded Images */}
              {files.length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-['Outfit'] text-lg font-medium text-[#0D2D54]">
                    Your Images ({files.length})
                  </h3>
                  {files.map((file, i) => (
                    <div
                      key={i}
                      data-testid={`uploaded-image-${i}`}
                      className="bg-[#FAFAF8] border border-[#E8E8E4] p-4 flex flex-col sm:flex-row gap-4"
                    >
                      <div className="w-full sm:w-28 h-20 bg-[#E8E8E4] flex-shrink-0 overflow-hidden">
                        {file.preview ? (
                          <img src={file.preview} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon size={20} className="text-[#6B7280]" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-[#0D2D54] font-medium truncate max-w-[200px]">
                            {file.original_filename}
                          </p>
                          <button
                            data-testid={`remove-image-${i}`}
                            onClick={() => removeFile(i)}
                            className="text-[#6B7280] hover:text-red-500 transition-colors"
                          >
                            <X size={16} />
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Select
                            value={file.roomType}
                            onValueChange={(val) => updateFile(i, "roomType", val)}
                          >
                            <SelectTrigger data-testid={`room-type-select-${i}`} className="h-9 text-xs border-[#E8E8E4] bg-white">
                              <SelectValue placeholder="Room type" />
                            </SelectTrigger>
                            <SelectContent>
                              {ROOM_TYPES.map((r) => (
                                <SelectItem key={r} value={r}>{r}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={file.style}
                            onValueChange={(val) => updateFile(i, "style", val)}
                          >
                            <SelectTrigger data-testid={`style-select-${i}`} className="h-9 text-xs border-[#E8E8E4] bg-white">
                              <SelectValue placeholder="Interior style" />
                            </SelectTrigger>
                            <SelectContent>
                              {INTERIOR_STYLES.map((s) => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Customer Info */}
              <div className="bg-[#FAFAF8] border border-[#E8E8E4] p-6 space-y-4">
                <h3 className="font-['Outfit'] text-lg font-medium text-[#0D2D54]">Your Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] text-[#6B7280] mb-2">Name *</label>
                    <input
                      type="text"
                      data-testid="customer-name-input"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className="w-full border border-[#E8E8E4] bg-white px-3 py-2.5 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none transition-colors"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.15em] text-[#6B7280] mb-2">Email *</label>
                    <input
                      type="email"
                      data-testid="customer-email-input"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      className="w-full border border-[#E8E8E4] bg-white px-3 py-2.5 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-[#6B7280] mb-2">Notes (optional)</label>
                  <textarea
                    data-testid="order-notes-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full border border-[#E8E8E4] bg-white px-3 py-2.5 text-sm text-[#0D2D54] focus:border-[#0D2D54] focus:outline-none transition-colors resize-none"
                    placeholder="Any special instructions..."
                  />
                </div>
              </div>
            </div>

            {/* Right: Order Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-white border border-[#E8E8E4] p-6">
                <h3 className="font-['Outfit'] text-lg font-medium text-[#0D2D54] mb-6">
                  Order Summary
                </h3>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6B7280]">Images</span>
                    <span data-testid="order-quantity" className="text-[#0D2D54] font-medium">{quantity}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6B7280]">Price per image</span>
                    <span data-testid="order-unit-price" className="text-[#0D2D54] font-medium">&pound;{unitPrice}</span>
                  </div>
                  {savings > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[#758C7B]">Bulk discount</span>
                      <span data-testid="order-savings" className="text-[#758C7B] font-medium">-&pound;{savings}</span>
                    </div>
                  )}
                  <div className="border-t border-[#E8E8E4] pt-4 flex justify-between">
                    <span className="text-sm font-medium text-[#0D2D54]">Total</span>
                    <span data-testid="order-total-price" className="font-['Outfit'] text-2xl font-medium text-[#0D2D54]">
                      &pound;{totalPrice.toFixed(2)}
                    </span>
                  </div>
                </div>

                {quantity >= BULK_THRESHOLD && (
                  <div data-testid="bulk-discount-banner" className="bg-[#758C7B]/15 border border-[#758C7B]/30 p-3 mb-6 text-center">
                    <p className="text-xs text-[#0D2D54] font-medium">
                      Bulk discount applied &mdash; &pound;55 per image
                    </p>
                  </div>
                )}

                <ul className="space-y-2 mb-6 text-xs text-[#6B7280]">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#758C7B] rounded-full" />
                    Delivered in 48 hours
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#758C7B] rounded-full" />
                    No subscription required
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#758C7B] rounded-full" />
                    Professional UK designers
                  </li>
                </ul>

                <button
                  data-testid="checkout-btn"
                  onClick={handleCheckout}
                  disabled={processing || files.length === 0}
                  className="w-full bg-[#0D2D54] text-white py-4 text-sm font-medium tracking-wide hover:bg-[#1A3D6B] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {processing ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Checkout &mdash; &pound;{totalPrice.toFixed(2)}
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>

                {quantity === 0 && (
                  <p className="text-center text-xs text-[#6B7280] mt-3">
                    Upload images to get started
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile sticky checkout bar */}
      {quantity > 0 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E8E8E4] p-4 z-40 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
          <button
            data-testid="mobile-checkout-btn"
            onClick={handleCheckout}
            disabled={processing || files.length === 0}
            className="w-full bg-[#0D2D54] text-white py-3.5 text-sm font-medium tracking-wide hover:bg-[#1A3D6B] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {processing ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Checkout &mdash; {quantity} image{quantity !== 1 ? "s" : ""} &mdash; &pound;{totalPrice.toFixed(2)}
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </div>
      )}
    </main>
  );
}
